// Authorized domains configuration for authentication

/**
 * Supported SSO provider types
 */
export type SSOProvider = 'google' | 'microsoft';

export const AUTHORIZED_DOMAINS = [
  'intuitionai.com',
  'intuitionai.co', 
  'outlook.com',
  'newmountaincapital.com',
  'alixpartners.com',
  'pulsecode.ca',
  // Add client domains here as needed
];

/**
 * SSO provider mapping - EXPLICIT configuration only
 * Every authorized domain MUST have an explicit SSO provider mapping
 */
export const SSO_PROVIDER_MAP: Record<string, SSOProvider> = {
  'intuitionai.com': 'google',
  'intuitionai.co': 'google',
  'outlook.com': 'microsoft',
  'newmountaincapital.com': 'microsoft',
  'alixpartners.com': 'microsoft',
  'pulsecode.ca': 'microsoft',
  // Add client-specific mappings here
} as const;

/**
 * Validates if an email domain is authorized for access
 */
export const validateEmailDomain = (email: string): boolean => {
  if (!email) return false;
  
  const domain = email.split('@')[1]?.toLowerCase().trim();
  if (!domain) return false;
  
  return AUTHORIZED_DOMAINS.includes(domain);
};

/**
 * Gets the SSO provider for the given email's domain
 * @throws Error if domain is not in SSO_PROVIDER_MAP
 */
export const getSSOProvider = (email: string): SSOProvider => {
  const domain = getDomainFromEmail(email);
  
  if (!SSO_PROVIDER_MAP[domain]) {
    throw new Error(`SSO provider not configured for domain "${domain}". Please contact your administrator.`);
  }
  
  return SSO_PROVIDER_MAP[domain];
};

/**
 * Gets the SSO provider for the given email's domain (safe version)
 * Returns undefined if domain is not configured - useful for analytics tracking
 */
export const getSSOProviderSafe = (email: string): SSOProvider | undefined => {
  const domain = getDomainFromEmail(email);
  return SSO_PROVIDER_MAP[domain];
};

/**
 * Extracts domain from email address
 */
export const getDomainFromEmail = (email: string): string => {
  return email.split('@')[1]?.toLowerCase().trim() || '';
};

/**
 * Gets a user-friendly error message for unauthorized domains
 */
export const getUnauthorizedDomainError = (email: string): string => {
  const domain = getDomainFromEmail(email);
  return `Access denied. The domain "${domain}" is not authorized to access this application. Only users from authorized organizations can sign in. Please contact your administrator if you believe this is an error.`;
};
